import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import {
    ArrowLeftDoubleIcon,
    ArrowRightDoubleIcon,
    InformationSquareIcon,
    Layers01Icon,
    NodeEditIcon,
    Settings05Icon,
    StrokeCenterIcon,
    SubnodeAddIcon,
} from '@hugeicons-pro/core-bulk-rounded'
import { Box, Button, Divider, IconButton, Typography } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import { enqueueSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useGetOrderItemsQuery, useUpdateOrderItemMutation } from '@/app/services/orderItems'
import { useGetSecondaryProcessesQuery } from '@/app/services/secondaryProcesses'
import { removeItemFromUpdateQueue } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId, selectWebStoreFoldingEnabled } from '@/app/slices/organisationSlice'
import SecondaryProcessesModal from '@/features/secondary-processes/SecondaryProcessesModal'

import DrawerItem from './DrawerItem'
import Folding from './Folding'
import Layers from './Layers'
import PartDetails from './PartDetails'
import PartSettings from './PartSettings'
import SecondaryProcesses from './SecondaryProcesses'

const classes = {
    drawerRoot: (drawerOpen) => ({
        width: drawerOpen ? 320 : 50,
        flexShrink: 0,

        '& .MuiDrawer-paper': {
            width: drawerOpen ? 320 : 50,
            height: 'calc(100% - 72px)',
            top: 72,
            boxSizing: 'border-box',
            borderRight: '1px solid hsl(0, 0%, 15%)',
            backgroundColor: 'hsl(0, 0%, 15%)',
        },
    }),
    icon: {
        color: 'action.active',
    },
}

const DrawingDoctorDrawer = ({ drawing, orderItemId, viewOnlyMode = false }) => {
    const { t } = useTranslation()
    const { orderId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)
    const foldingEnabled = useSelector(selectWebStoreFoldingEnabled)

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [drawerOpen, setDrawerOpen] = useState(true)
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
    const [itemSecondaryProcesses, setItemSecondaryProcesses] = useState([])
    const [showFolding, setShowFolding] = useState(orderItem?.numberOfFolds > 0)

    const [updateOrderItem] = useUpdateOrderItemMutation()
    const { data: organisationSecondaryProcesses } = useGetSecondaryProcessesQuery({ organisationId })

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)
    const isRotary = Boolean(orderItem) && 'profile' in orderItem && Boolean(orderItem?.profile)

    const hasEnabledCustomProcesses =
        organisationSecondaryProcesses?.some(
            (process) => !process.isDeleted && process.webStoreEnabled && process.customerVisible
        ) ?? false

    const handleUpdateOrderItem = async (orderItem) => {
        try {
            const orderId = orderItem.quoteId
            await updateOrderItem({
                organisationId,
                orderId,
                orderItemId: orderItem?.id,
                orderItem,
            }).unwrap()
        } catch (error) {
            enqueueSnackbar(t('Failed to update the order item'), {
                variant: 'error',
            })
        }
    }

    const handleEditButtonClick = (event) => {
        event.stopPropagation()
        setIsEditDialogOpen(true)
    }

    const onSaveCloseEditDialog = async (processes) => {
        setIsEditDialogOpen(false)
        //handle empty processes
        if (!processes?.length) {
            setItemSecondaryProcesses([])
            setShowFolding(false)
            await handleUpdateOrderItem({
                ...orderItem,
                secondaryProcesses: [],
                numberOfFolds: 0,
                maxFoldLength: 0,
                foldingSource: 'Manual',
            })
            dispatch(removeItemFromUpdateQueue(orderItemId))
        }

        if (processes?.length) {
            const hasFolding = processes.find((process) => process.secondaryProcessId === 'Folding')
            const otherProcesses = processes?.filter((process) => process.secondaryProcessId !== 'Folding')

            if (hasFolding) {
                setShowFolding(true)
            } else {
                setShowFolding(false)
            }

            setItemSecondaryProcesses(otherProcesses)
            await handleUpdateOrderItem({
                ...orderItem,
                secondaryProcesses: otherProcesses,
                numberOfFolds: hasFolding ? orderItem.numberOfFolds || 1 : 0,
                maxFoldLength: hasFolding ? orderItem.maxFoldLength || 0 : 0,
                foldingSource: hasFolding ? orderItem.foldingSource || 'Manual' : 'Manual',
            })
        }
    }

    const getEditIcon = () => {
        return (
            <Button
                aria-label="edit"
                color="secondary"
                data-testid="secondary-processes-add-button"
                size="small"
                startIcon={
                    <HugeiconsIcon
                        icon={NodeEditIcon}
                        size={16}
                    />
                }
                sx={{ p: 0 }}
                title={t('Edit secondary processes')}
                onClick={(event) => handleEditButtonClick(event)}
                onFocus={(event) => event.stopPropagation()}
            >
                {t('Edit')}
            </Button>
        )
    }

    useEffect(() => {
        setItemSecondaryProcesses(orderItem?.secondaryProcesses || [])
        setShowFolding(orderItem?.numberOfFolds > 0)
    }, [orderItem?.secondaryProcesses, orderItem?.numberOfFolds])

    // Hide drawer completely in view-only mode
    if (viewOnlyMode) {
        return null
    }

    return (
        <>
            <Drawer
                anchor="left"
                disableAutoFocus={true}
                disableEnforceFocus={true}
                hideBackdrop={false}
                open={drawerOpen}
                sx={classes.drawerRoot(drawerOpen)}
                variant="permanent"
                onClose={() => setDrawerOpen(false)}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: drawerOpen ? 'right' : 'center',
                    }}
                >
                    {drawerOpen ? (
                        <IconButton
                            size="small"
                            sx={{
                                color: 'action.active',
                                margin: '4px 16px',
                                cursor: 'pointer',
                            }}
                        >
                            <HugeiconsIcon
                                className=""
                                icon={ArrowLeftDoubleIcon}
                                size={20}
                                onClick={() => setDrawerOpen(false)}
                            />
                        </IconButton>
                    ) : (
                        <IconButton
                            size="small"
                            sx={{
                                color: 'action.active',
                                margin: '4px 16px',
                                cursor: 'pointer',
                            }}
                        >
                            <HugeiconsIcon
                                className=""
                                icon={ArrowRightDoubleIcon}
                                size={20}
                                onClick={() => setDrawerOpen(true)}
                            />
                        </IconButton>
                    )}
                </Box>
                <DrawerItem
                    drawerOpen={drawerOpen}
                    handleOpenDrawer={() => setDrawerOpen(true)}
                    icon={
                        <HugeiconsIcon
                            icon={InformationSquareIcon}
                            size={20}
                        />
                    }
                    title={t('Part details')}
                >
                    <PartDetails
                        drawing={drawing}
                        orderItemId={orderItemId}
                    />
                </DrawerItem>
                {!isRotary ? (
                    <DrawerItem
                        drawerOpen={drawerOpen}
                        handleOpenDrawer={() => setDrawerOpen(true)}
                        icon={
                            <HugeiconsIcon
                                icon={Layers01Icon}
                                size={20}
                            />
                        }
                        title={t('Layers')}
                    >
                        <Layers
                            drawing={drawing}
                            orderItemId={orderItemId}
                        />
                    </DrawerItem>
                ) : null}

                <DrawerItem
                    drawerOpen={drawerOpen}
                    handleOpenDrawer={() => setDrawerOpen(true)}
                    icon={
                        <HugeiconsIcon
                            icon={Settings05Icon}
                            size={20}
                        />
                    }
                    title={t('Settings')}
                >
                    <PartSettings orderItemId={orderItemId} />
                </DrawerItem>

                <DrawerItem
                    drawerOpen={drawerOpen}
                    editButton={
                        !isPartFromLibrary && (itemSecondaryProcesses?.length || showFolding) ? getEditIcon() : null
                    }
                    handleOpenDrawer={() => setDrawerOpen(true)}
                    icon={
                        <HugeiconsIcon
                            icon={StrokeCenterIcon}
                            size={20}
                        />
                    }
                    title={t('Secondary processes')}
                >
                    {!itemSecondaryProcesses?.length &&
                    !showFolding &&
                    (hasEnabledCustomProcesses || (!isRotary && foldingEnabled)) ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flex: 1,
                                paddingBlock: 2,
                            }}
                        >
                            <Button
                                color="secondary"
                                data-testid="secondary-processes-add-button"
                                disabled={isPartFromLibrary}
                                startIcon={<HugeiconsIcon icon={SubnodeAddIcon} />}
                                variant="contained"
                                onClick={(event) => handleEditButtonClick(event)}
                                onFocus={(event) => event.stopPropagation()}
                            >
                                {t('Add secondary processes')}
                            </Button>
                        </Box>
                    ) : null}

                    {!itemSecondaryProcesses?.length &&
                    !showFolding &&
                    !hasEnabledCustomProcesses &&
                    (isRotary || !foldingEnabled) ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flex: 1,
                                paddingBlock: 2,
                            }}
                        >
                            <Typography
                                color="text.secondary"
                                variant="body2"
                            >
                                {t('No secondary processes available')}
                            </Typography>
                        </Box>
                    ) : null}

                    {showFolding ? <Folding orderItemId={orderItemId} /> : null}
                    {showFolding && itemSecondaryProcesses?.length > 0 ? (
                        <Divider sx={{ opacity: 0.1, marginBlock: 1.5 }} />
                    ) : null}

                    <SecondaryProcesses orderItemId={orderItemId} />
                </DrawerItem>
            </Drawer>
            {isEditDialogOpen ? (
                <SecondaryProcessesModal
                    accentColor="primary"
                    foldingEnabled={foldingEnabled}
                    isRotary={isRotary}
                    open={isEditDialogOpen}
                    orderItemId={orderItemId}
                    secondaryProcesses={orderItem?.secondaryProcesses}
                    showFolding={showFolding}
                    onSaveClose={onSaveCloseEditDialog}
                />
            ) : null}
        </>
    )
}

DrawingDoctorDrawer.propTypes = {
    drawing: PropTypes.object,
    orderItemId: PropTypes.string,
    viewOnlyMode: PropTypes.bool,
}

export default memo(DrawingDoctorDrawer)
